import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import { getUserSubscriptionStatus } from "../../utils/data.utils";
import OptimizedImage from "../common/OptimizedImage";
import { useInputMode } from "../../utils/hooks/useInputMode";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";
import { sendPlayIntent } from "../../services/mediaAnalyticsService";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";
import { hasAuthenticatedSession } from "../../services/authStorage";

const EpisodeItem = ({
  item,
  onFocus,
  slug,
  overlayConfig,
  canPlayLoading,
  likeOrDislikeArray = [],
}) => {
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () =>
      typeof overlayConfig.action === "function" && overlayConfig.action(),
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const handleClick = () => {
    if (typeof overlayConfig.action === "function") {
      overlayConfig.action();
    }
  };

  // Get optimized image with LQIP support
  const episodeImageData = getOptimizedImageForType('episodeCover', item.data?.meta?.contentPhotos, 'TV', { width: 350, quality: 90, format: 'jpeg' });

  // Find matching like/dislike entry for this episode by season and episode number
  const episodeAction = likeOrDislikeArray.find(
    (action) => action.season === item.seasonNumber && action.episode === item.episodeNumber
  );
  const likeOrDislikeStatus = episodeAction?.likeOrDislike;

  return (
    <div className="legacy-episode-item" ref={ref}>
      <div className={"legacy-episode-card" + (focused ? " is-focused" : "")} onClick={handleClick} onMouseEnter={handleMouseEnter}>
        <OptimizedImage
          src={episodeImageData.src}
          alt={item.title}
          style={{
            display: 'block',
            width: '100%',
            height: '250px',
            objectFit: 'cover',
          }}
        />

        {/* Sentiment indicator - non-focusable visual feedback */}
        {likeOrDislikeStatus === 1 && (
          <div className="legacy-episode-sentiment legacy-episode-sentiment-like">
            <img src="/svg/thumbs-up-filled.svg" alt="Liked" width={32} height={32} />
          </div>
        )}
        {likeOrDislikeStatus === 2 && (
          <div className="legacy-episode-sentiment legacy-episode-sentiment-dislike">
            <img src="/svg/thumbs-down-filled.svg" alt="Disliked" width={32} height={32} />
          </div>
        )}
        <div className="legacy-episode-overlay">
          <span className="legacy-episode-overlay-label">
            {!canPlayLoading && overlayConfig?.label}
          </span>
        </div>
      </div>
      <p className="legacy-episode-name">{item.episodeName}</p>
    </div>
  );
};

export const EpisodeSlider = ({
  movies = [],
  onFocus,
  rowTitle,
  movie,
  slug,
}) => {
  const { ref, focusKey } = useFocusable({ onFocus });
  const scrollingRef = useRef(null);
  const innerRef = useRef(null);
  const [canPlay, setCanPlay] = useState();
  const [canPlayLoading, setCanPlayLoading] = useState(true);
  const [likeOrDislikeArray, setLikeOrDislikeArray] = useState([]);
  const isLogin = hasAuthenticatedSession();

  // Fetch content actions to get like/dislike status for each episode
  const fetchContentActions = async () => {
    if (!isLogin || !slug) return;
    try {
      const response = await httpService.get(getCMSApiRoute("action/content"), {
        params: { contentKey: slug },
      });
      if (response.status === 200) {
        const { data } = response.data;
        setLikeOrDislikeArray(data?.userActions?.likeOrDislike || []);
      }
    } catch (error) {
      console.log('[EpisodeSlider] Failed to fetch content actions:', error);
    }
  };

  const updateSubCondition = async () => {
    const subStatus = await getUserSubscriptionStatus();
    setCanPlay(subStatus > 0);
    setCanPlayLoading(false);
  };

  // Get isMouseFocus to guard horizontal scroll on hover
  const { isMouseFocus } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      // Skip horizontal scroll adjustment when focus originated from mouse hover
      if (isMouseFocus) return;
      var x = props.x;
      if (x < 0) {
        innerRef.current.style.transform =
          "translateX(" + Math.abs(x - 300) + "px)";
      } else {
        innerRef.current.style.transform = "translateX(0px)";
      }
    },
    [scrollingRef, isMouseFocus]
  );

  const navigate = useNavigate();

  const getActionOverlay = function (s, e) {
    var trackEpisodePlayIntent = function () {
      sendPlayIntent({
        contentKey: slug,
        mode: movie?.isLive === 1 || movie?.isLive === true ? "live" : "vod",
        contentKind: movie?.contentType?.kind || "series",
        surface: "content_detail",
        source: "episode_play_button",
      });
    };

    if (!isLogin) {
      return {
        label: "ورود و تماشا",
        action: function () {
          navigate("/login?ref=" + encodeURI("/watch/" + slug + "/" + s + "/" + e));
        },
      };
    } else if (movie.isFree === 1) {
      return {
        label: "تماش کن",
        action: function () {
          trackEpisodePlayIntent();
          navigate("/watch/" + slug + "/" + s + "/" + e);
        },
      };
    } else if (movie.isFree !== 1 && canPlay) {
      return {
        label: "تماشا کن",
        action: function () {
          trackEpisodePlayIntent();
          navigate("/watch/" + slug + "/" + s + "/" + e);
        },
      };
    } else {
      return {
        label: "خرید اشتراک و تماشا",
        action: function () {
          trackGoogleAnalyticsEvent("purchase_started", {
            purchase_type: "subscription",
            source: "episode_play_button",
            content_id: slug,
            season_number: s,
            episode_number: e,
          });
          navigate("/subscription");
        },
      };
    }
  };

  useEffect(function () {
    if (movies) {
      updateSubCondition();
      fetchContentActions();
    }
  }, []);

  if (!movies) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-episode-row" ref={ref}>
        <h3 className="legacy-episode-row-title">{rowTitle}</h3>
        <div className="legacy-episode-row-scroller" ref={scrollingRef}>
          <div className="legacy-episode-row-content" ref={innerRef}>
            {movies?.map(function (item) {
              return (
                <EpisodeItem
                  item={item}
                  key={item.id}
                  onFocus={onAssetFocus}
                  slug={slug}
                  overlayConfig={getActionOverlay(
                    item.seasonNumber,
                    item.episodeNumber
                  )}
                  canPlayLoading={canPlayLoading}
                  likeOrDislikeArray={likeOrDislikeArray}
                />
              );
            })}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
